import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  FiltersActionsApply,
  RideStationDragOverlay,
  TopHeaderCalendar,
  useTableDefsRides,
} from "@ryde/controls";
import {
  BulkAiAdvisorDrawer,
  BulkUpdateFormDrawer,
  FinalSchemaService,
  PageHeader,
  PageHeaderFilters,
  TableSettingsDrawerWrapperGeneric,
} from "@ryde/layout";
import {
  PageHeaderContext,
  useAiAdvisor,
  usePageContext,
  useRidesActions,
} from "@ryde/redux";
import {
  bulkCloneFormDrawerAF,
  bulkJourneyAssignFormDrawerAF,
  bulkOperationsDrawerAF,
  bulkRebuildRideDrawerAtom,
  bulkRebuildRoutesCustomDrawerAtom,
  bulkRecalculatePriceOptionsDrawerAF,
  bulkRecalculateSupplierPriceOptionsDrawerAF,
  bulkUpdateFormDrawerAF,
  bulkUpdateStationsDrawerAtom,
  drawersAtom,
  DrawerType,
  IRideTableDto,
  PLURAL_KEY,
  tableSettingsDrawerAF,
} from "@ryde/shared";

import {
  BulkCloneFormRidesDrawer,
  BulkJourneyAssignFormRidesDrawer,
  BulkOperationsRidesDrawer,
  BulkRebuildRidesDrawer,
  BulkRebuildRoutesCustomRidesDrawer,
  BulkRecalculatePriceOptionsRidesDrawer,
  BulkRecalculateSupplierPriceOptionsRidesDrawer,
  BulkUpdateStationsRidesDrawer,
} from "./drawers";
import { useRideSoftValidationModal, useRidesPage } from "./hooks";
import { RideResolveNearBy } from "./ride-resolve-nearby";
import { RidesKanbanSwitchRoutes } from "./routes";
import { RidesFloatingMenu, RidesPanelTop } from "./search";
import { RidesDragOverlaysWrapper } from "./shared";

export const RidesPageContent: FC = () => {
  const { entity } = usePageContext();
  const { t } = useTranslation();
  const { fetchRides } = useRidesActions();
  const { isBulkAIAdvisorDrawerOpen } = useAiAdvisor(entity);

  useRidesPage(entity);

  useRideSoftValidationModal();

  const { tableDefsRides } = useTableDefsRides();
  const tableSettingsDrawerOpened = useRecoilValue(
    tableSettingsDrawerAF(entity),
  );
  const bulkOperationsDrawerOpened = useRecoilValue(
    bulkOperationsDrawerAF(entity),
  );

  const bulkUpdateFormDrawerOpened = useRecoilValue(
    bulkUpdateFormDrawerAF(entity),
  );

  const bulkCloneFormDrawerOpened = useRecoilValue(
    bulkCloneFormDrawerAF(entity),
  );

  const bulkJourneyAssignFormDrawerOpened = useRecoilValue(
    bulkJourneyAssignFormDrawerAF(entity),
  );

  const bulkRecalculatePriceOptionsDrawerOpened = useRecoilValue(
    bulkRecalculatePriceOptionsDrawerAF(entity),
  );

  const bulkRecalculateSupplierPriceOptionsDrawerOpened = useRecoilValue(
    bulkRecalculateSupplierPriceOptionsDrawerAF(entity),
  );

  const bulkRebuildRideDrawerOpened = useRecoilValue(bulkRebuildRideDrawerAtom);
  const bulkRebuildRoutesCustomDrawerOpened = useRecoilValue(
    bulkRebuildRoutesCustomDrawerAtom,
  );

  const bulkUpdateStationsDrawerOpened = useRecoilValue(
    bulkUpdateStationsDrawerAtom,
  );
  const drawers = useRecoilValue(drawersAtom);
  const isRideEditDrawerOpen = useMemo(
    () =>
      drawers.some(
        ({ drawerProps }) => drawerProps.type === DrawerType.RideEditForm,
      ),
    [drawers],
  );

  const onFiltersApply = useEvent((): void => {
    fetchRides();
  });

  const rideHeaderContextValue = useMemo(() => {
    return {
      onFiltersApply: onFiltersApply,
      onFiltersClear: onFiltersApply,
      onSearchApply: onFiltersApply,
    };
  }, [onFiltersApply]);

  return (
    <>
      <FinalSchemaService />
      <PageHeader
        mainTitle={t(`ride.${PLURAL_KEY}`)}
        topWidgetComponent={<TopHeaderCalendar entity={entity} />}
        bottomWidgetComponent={<RidesPanelTop />}
      >
        <PageHeaderContext.Provider value={rideHeaderContextValue}>
          <PageHeaderFilters Actions={<FiltersActionsApply />} />
        </PageHeaderContext.Provider>
      </PageHeader>

      <RidesKanbanSwitchRoutes />
      {tableSettingsDrawerOpened && (
        <TableSettingsDrawerWrapperGeneric<IRideTableDto>
          tableDefs={tableDefsRides || []}
        />
      )}
      {bulkOperationsDrawerOpened && <BulkOperationsRidesDrawer />}
      {bulkUpdateFormDrawerOpened && <BulkUpdateFormDrawer />}
      {bulkCloneFormDrawerOpened && <BulkCloneFormRidesDrawer />}
      {bulkJourneyAssignFormDrawerOpened && (
        <BulkJourneyAssignFormRidesDrawer />
      )}
      {bulkRecalculatePriceOptionsDrawerOpened && (
        <BulkRecalculatePriceOptionsRidesDrawer />
      )}
      {bulkRecalculateSupplierPriceOptionsDrawerOpened && (
        <BulkRecalculateSupplierPriceOptionsRidesDrawer />
      )}
      {bulkRebuildRideDrawerOpened && <BulkRebuildRidesDrawer />}
      {bulkRebuildRoutesCustomDrawerOpened && (
        <BulkRebuildRoutesCustomRidesDrawer />
      )}
      {bulkUpdateStationsDrawerOpened && <BulkUpdateStationsRidesDrawer />}
      {isBulkAIAdvisorDrawerOpen && <BulkAiAdvisorDrawer />}
      <RidesDragOverlaysWrapper />
      {!isRideEditDrawerOpen && <RideStationDragOverlay />}
      <RidesFloatingMenu />
      <RideResolveNearBy />
    </>
  );
};
